import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { getFileName } from '../../utils/imageUtils';
import { getAssetNameFromUrl } from '../../utils/assetUtils';
import { useData } from '../../contexts/DataContext';

export default function SummaryRenderer({ config, data, darkMode = false }) {
    const { adminData, locationData } = useData();
    const {
        risks, steps, pbms, tools, permits,
        lotoSteps, lotoRequired, discipline,
        qualification, hazards, specificLoto, hygieneCategories,
        liftingPlanType
    } = data || {};
    const [selectedImage, setSelectedImage] = useState(null);

    // Theme colors
    const theme = {
        textPrimary: darkMode ? 'text-slate-100' : 'text-slate-800',
        textSecondary: darkMode ? 'text-slate-400' : 'text-slate-600',
        border: darkMode ? 'border-slate-700' : 'border-slate-300',
        bgSubtle: darkMode ? 'bg-slate-800' : 'bg-slate-100',
        bgCard: darkMode ? 'bg-slate-800' : 'bg-white',
        headingBorder: darkMode ? 'border-slate-700' : 'border-slate-300',
        accentBlue: darkMode ? 'text-blue-400' : 'text-blue-700',
        accentRed: darkMode ? 'text-red-400' : 'text-red-700',
        accentGreen: darkMode ? 'text-green-400' : 'text-green-600',
    };

    // Determine spacing classes
    const getSpacingClass = () => {
        switch (config.spacing) {
            case 'compact': return 'gap-4';
            case 'spacious': return 'gap-16';
            case 'aggressive': return 'gap-24';
            default: return 'gap-8'; // normal
        }
    };

    const spacingClass = getSpacingClass();

    const formatText = (text) => {
        if (!text) return '';
        // Replace colon not followed by space with colon + space
        return text.replace(/:(?!\s)/g, ': ');
    };

    const getSmartLabel = (url) => {
        // Resolve latest hierarchy for this permit's location from global context
        const contextHierarchy = (data.location && adminData?.locations?.[data.location]?.hierarchy) ||
            (locationData?.name === data.location ? locationData?.hierarchy : null);

        // Resolution prioritizing Hierarchy (via contextHierarchy inclusion)
        const resolvedName = getAssetNameFromUrl(url, {
            ...data,
            hierarchy: contextHierarchy || data.hierarchy
        });

        return resolvedName || getFileName(url) || 'Onbekende foto';
    };

    const renderSection = (sectionId) => {
        switch (sectionId) {
            case 'opdracht':
                return (
                    <div>
                        <h3 className={`font-bold text-lg border-b-2 ${theme.headingBorder} mb-6 pb-2 ${theme.textPrimary}`}>📋 Opdracht</h3>
                        <div className="space-y-4">
                            <div>
                                <span className={`font-semibold ${theme.textSecondary} text-sm`}>Doel:&nbsp;</span>
                                <span className={`text-base ${theme.textPrimary} break-words`}>{data.goal}</span>
                            </div>
                            <div>
                                <span className={`font-semibold ${theme.textSecondary} text-sm`}>Omschrijving:&nbsp;</span>
                                <span className={`text-base ${theme.textPrimary} break-words`}>{data.description}</span>
                            </div>
                        </div>
                    </div>
                );
            case 'locatie':
                return (
                    <div>
                        <style>{`
                            a.force-blue-link, 
                            a.force-blue-link:visited, 
                            a.force-blue-link:hover, 
                            a.force-blue-link:active, 
                            a.force-blue-link:focus {
                                color: #38BDF8 !important;
                                text-decoration-color: #38BDF8 !important;
                            }
                        `}</style>
                        <h3 className={`font-bold text-lg border-b-2 ${theme.headingBorder} mb-6 pb-2 ${theme.textPrimary}`}>📍 Locatie & Object</h3>
                        <div className="space-y-4">
                            <div className="text-base text-slate-300 flex items-center gap-2 flex-wrap font-mono bg-slate-800/50 p-3 rounded border border-slate-700">
                                <span className={theme.textPrimary}>{data.location || 'Locatie'}</span>
                                {data.hall && <span className="text-slate-500">→</span>}
                                {data.hall && <span className={theme.textPrimary}>{data.hall}</span>}
                                {data.line && <span className="text-slate-500">→</span>}
                                {data.line && <span className={theme.textPrimary}>{data.line}</span>}
                                {data.subline && <span className="text-slate-500">→</span>}
                                {data.subline && (
                                    <span className="flex items-center gap-2">
                                        <span className={theme.textPrimary}>{data.subline}</span>
                                        {/* Display ALL links associated with the Subline context */}
                                        {data.sublineDocLink && (
                                            <a href={data.sublineDocLink} target="_blank" rel="noopener noreferrer"
                                                className="force-blue-link hover:text-sky-300" title="Technisch / Algemeen">
                                                🛠️
                                            </a>
                                        )}
                                        {data.electroDocLink && (
                                            <a href={data.electroDocLink} target="_blank" rel="noopener noreferrer"
                                                className="force-blue-link hover:text-sky-300" title="Elektro">
                                                ⚡
                                            </a>
                                        )}
                                        {data.manualDocLink && (
                                            <a href={data.manualDocLink} target="_blank" rel="noopener noreferrer"
                                                className="force-blue-link hover:text-sky-300" title="Handleiding">
                                                📘
                                            </a>
                                        )}
                                    </span>
                                )}
                            </div>
                            <div>
                                <div className="mb-1"><span className={`font-semibold ${theme.textSecondary}`}>Onderdeel:&nbsp;</span><span className={theme.textPrimary}>{data.component}</span></div>
                                <div className="flex flex-wrap gap-3 mt-2">
                                    {data.componentDocLink && (
                                        <a
                                            href={data.componentDocLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`flex items-center gap-2 text-sm font-bold force-blue-link hover:underline bg-blue-900/20 px-2 py-1 rounded border border-blue-900/50`}
                                        >
                                            📘 Handleiding
                                        </a>
                                    )}
                                    {data.electroDocLink && (
                                        <a
                                            href={data.electroDocLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`flex items-center gap-2 text-sm font-bold force-blue-link hover:underline bg-blue-900/20 px-2 py-1 rounded border border-blue-900/50`}
                                        >
                                            ⚡ Elektro Schema
                                        </a>
                                    )}
                                    {data.technischDocLink && (
                                        <a
                                            href={data.technischDocLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`flex items-center gap-2 text-sm font-bold force-blue-link hover:underline bg-blue-900/20 px-2 py-1 rounded border border-blue-900/50`}
                                        >
                                            🛠️ Technisch
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'taak':
                return (
                    <div>
                        <h3 className={`font-bold text-lg border-b-2 ${theme.headingBorder} mb-6 pb-2 ${theme.textPrimary}`}>⚡ Taak & Kwalificatie</h3>
                        <div className={`font-semibold ${theme.accentBlue} text-xl mb-4`}>{data.task}</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <span className={`font-semibold ${theme.textSecondary} text-sm`}>Discipline:&nbsp;</span>
                                <span className={`text-base ${theme.textPrimary}`}>{discipline || '-'}</span>
                            </div>
                            <div>
                                <span className={`font-semibold ${theme.textSecondary} text-sm`}>Kwalificatie:&nbsp;</span>
                                <span className={`font-bold text-base ${qualification === 'VP' || qualification === 'VOP' ? theme.accentRed : theme.accentGreen}`}>
                                    {qualification || 'Geen'}
                                </span>
                            </div>
                        </div>

                        {/* Spacer for visual separation */}
                        <div style={{ height: '40px', width: '100%', display: 'block' }}></div>

                        {hazards && (
                            <div className="grid grid-cols-2 gap-3">
                                <div className={`px-3 py-2.5 rounded-lg font-medium ${hazards.electrical ? 'bg-red-900/30 text-red-400 border border-red-800' : `${theme.bgSubtle} ${theme.textSecondary}`}`}>
                                    ⚡ Elektrisch: {hazards.electrical ? 'JA' : 'Nee'}
                                </div>
                                <div className={`px-3 py-2.5 rounded-lg font-medium ${hazards.pressure ? 'bg-red-900/30 text-red-400 border border-red-800' : `${theme.bgSubtle} ${theme.textSecondary}`}`}>
                                    💨 Restdruk: {hazards.pressure ? 'JA' : 'Nee'}
                                </div>
                                <div className={`px-3 py-2.5 rounded-lg font-medium ${hazards.movement ? 'bg-red-900/30 text-red-400 border border-red-800' : `${theme.bgSubtle} ${theme.textSecondary}`}`}>
                                    ⚙️ Beweging: {hazards.movement ? 'JA' : 'Nee'}
                                </div>
                                <div className={`px-3 py-2.5 rounded-lg font-medium ${hazards.heat ? 'bg-red-900/30 text-red-400 border border-red-800' : `${theme.bgSubtle} ${theme.textSecondary}`}`}>
                                    🔥 Hitte: {hazards.heat ? 'JA' : 'Nee'}
                                </div>
                                <div className={`col-span-2 px-3 py-2.5 rounded-lg font-medium ${hazards.chemicals ? 'bg-red-900/30 text-red-400 border border-red-800' : `${theme.bgSubtle} ${theme.textSecondary}`}`}>
                                    🧪 Chemie: {hazards.chemicals ? 'JA' : 'Nee'}
                                </div>
                            </div>
                        )}

                        {/* Qualification Warning */}
                        {(() => {
                            const personnel = data.users || []; // Assuming users are passed in data
                            const riskLevel = data.riskLevel || 1;
                            const monteurName = data.monteur1;

                            if (riskLevel > 1 && monteurName) {
                                const person = personnel.find(p => p.Functie === monteurName);
                                if (person) {
                                    const userCerts = person.certificates || [];
                                    let isValid = true;
                                    let requiredCert = '';

                                    if (riskLevel === 2) {
                                        isValid = userCerts.includes('PED') || userCerts.includes('NEN-EN 1090');
                                        requiredCert = 'PED of NEN-EN 1090';
                                    } else if (riskLevel === 3) {
                                        isValid = userCerts.includes('Hygiëne');
                                        requiredCert = 'Hygiëne';
                                    }

                                    if (!isValid) {
                                        return (
                                            <div className="mt-4 p-3 bg-red-900/30 border-l-4 border-red-500 text-red-400 rounded shadow-sm">
                                                <div className="font-bold flex items-center gap-2">
                                                    ⛔ WAARSCHUWING: Onbevoegd Personeel
                                                </div>
                                                <div className="text-sm mt-1">
                                                    {monteurName} mist het vereiste certificaat voor Risiconiveau {riskLevel}.
                                                </div>
                                                <div className="text-xs font-semibold mt-1">
                                                    Vereist: {requiredCert}
                                                </div>
                                            </div>
                                        );
                                    }
                                }
                            }
                            return null;
                        })()}



                        {/* Task Images */}
                        {data.taskImageUrls && data.taskImageUrls.length > 0 && (
                            <div className="mt-12">
                                <h4 className={`font-bold text-sm ${theme.textSecondary} mb-2 uppercase tracking-wide`}>📸 Asset/Onderdeel foto's:</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    {data.taskImageUrls.map((url, index) => {
                                        const label = getSmartLabel(url);

                                        return (
                                            <div key={index} className={`border ${theme.border} rounded-lg overflow-hidden ${theme.bgSubtle} relative group flex flex-col`}>
                                                <img
                                                    src={url}
                                                    alt={`Situatie ${index + 1}`}
                                                    className="w-full h-48 object-contain cursor-pointer hover:opacity-90 transition-opacity bg-black/20"
                                                    onClick={() => setSelectedImage(url)}
                                                />
                                                <div className="bg-slate-800/80 p-2 text-center border-t border-slate-700">
                                                    <div className="text-xs font-bold text-white truncate" title={label}>
                                                        {label}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {/* Legacy Single Image Fallback */}
                        {!data.taskImageUrls && data.taskImageUrl && (
                            <div className="mt-12">
                                <h4 className={`font-bold text-sm ${theme.textSecondary} mb-2 uppercase tracking-wide`}>📸 Asset/Onderdeel foto:</h4>
                                <div className={`border ${theme.border} rounded-lg overflow-hidden ${theme.bgSubtle} inline-block relative group flex flex-col`}>
                                    <img
                                        src={data.taskImageUrl}
                                        alt="Situatie"
                                        className="max-w-full h-auto max-h-96 object-contain cursor-pointer hover:opacity-90 transition-opacity bg-black/20"
                                        onClick={() => setSelectedImage(data.taskImageUrl)}
                                    />
                                    {(() => {
                                        const url = data.taskImageUrl;
                                        const label = getSmartLabel(url);
                                        return (
                                            <div className="bg-slate-800/80 p-2 text-center border-t border-slate-700 w-full">
                                                <div className="text-xs font-bold text-white truncate" title={label}>
                                                    {label}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>
                );
            case 'personeel':
                return (
                    <div>
                        <h3 className={`font-bold text-lg border-b-2 ${theme.headingBorder} mb-6 pb-2 ${theme.textPrimary}`}>👥 Personeel</h3>
                        <div className="space-y-4">
                            <div><span className={`font-semibold ${theme.textSecondary}`}>Werkvoorbereider:</span> <span className={`ml-2 ${theme.textPrimary}`}>{data.werkvoorbereider || 'N.v.t.'}</span></div>
                            <div><span className={`font-semibold ${theme.textSecondary}`}>Werkverantwoordelijke:</span> <span className={`ml-2 ${theme.textPrimary}`}>{data.werkverantwoordelijke || 'N.v.t.'}</span></div>
                            <div><span className={`font-semibold ${theme.textSecondary}`}>Uitvoerende 1:</span> <span className={`ml-2 ${theme.textPrimary}`}>{data.monteur1 || 'N.v.t.'}</span></div>
                            {data.monteur2 && (
                                <div><span className={`font-semibold ${theme.textSecondary}`}>Uitvoerende 2:</span> <span className={`ml-2 ${theme.textPrimary}`}>{data.monteur2}</span></div>
                            )}
                        </div>
                    </div>
                );
            case 'pbms':
                return (
                    <div>
                        <h3 className={`font-bold text-lg border-b-2 ${theme.headingBorder} mb-6 pb-2 ${theme.textPrimary}`}>🦺 Benodigde PBM's</h3>
                        <ul className="space-y-4">
                            {(pbms || []).map((pbm, i) => (
                                <li key={i} className={`text-base flex items-start ${theme.textPrimary}`}>
                                    <span className="mr-2">•</span>
                                    <span>{pbm.PBM}</span>
                                </li>
                            ))}
                            {(!pbms || pbms.length === 0) && <li className={`${theme.textSecondary} italic flex items-start`}><span className="mr-2">•</span><span>Geen PBM's geselecteerd</span></li>}
                        </ul>
                    </div>
                );
            case 'gereedschap':
                return (
                    <div>
                        <h3 className={`font-bold text-lg border-b-2 ${theme.headingBorder} mb-6 pb-2 ${theme.textPrimary}`}>🔧 Gereedschap</h3>
                        <ul className="space-y-4">
                            {(tools || []).map((tool, i) => (
                                <li key={i} className={`text-base flex items-start ${theme.textPrimary}`}>
                                    <span className="mr-2">•</span>
                                    <span>{typeof tool === 'object' ? (tool.Tool || tool.name) : tool}</span>
                                </li>
                            ))}
                            {(!tools || tools.length === 0) && <li className={`${theme.textSecondary} italic flex items-start`}><span className="mr-2">•</span><span>Geen gereedschap geselecteerd</span></li>}
                        </ul>
                    </div>
                );
            case 'vergunningen':
                if (!permits || permits.length === 0) return null;
                return (
                    <div>
                        <h3 className={`font-bold text-lg border-b-2 border-red-300 mb-6 pb-2 ${theme.accentRed}`}>⚠️ Vereiste Werkplannen</h3>
                        <ul className="space-y-4">
                            {permits.map((permit, i) => {
                                const permitName = typeof permit === 'object' ? (permit.name || permit.label || 'Onbekend werkplan') : permit;
                                return (
                                    <li key={i} className={`font-semibold ${theme.accentRed} text-base flex items-start`}>
                                        <span className="mr-2">•</span>
                                        <span>{permitName}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                );
            case 'loto':
                if (!lotoRequired && (!specificLoto || specificLoto.length === 0) && (!lotoSteps || lotoSteps.length === 0)) return null;
                return (
                    <div>
                        <h3 className={`font-bold text-lg border-b-2 border-yellow-300 mb-6 pb-2 ${theme.textPrimary} flex items-center gap-2`}>
                            ⚠️ LOTO/LOTOTO Procedures
                        </h3>
                        {specificLoto && specificLoto.length > 0 && (
                            <div className="mb-6">
                                <h4 className={`font-bold text-sm ${theme.textSecondary} mb-2 uppercase tracking-wide`}>Specifieke Isolatie Instructies:</h4>
                                <div className="overflow-x-auto">
                                    <table className={`w-full min-w-[600px] border-collapse border ${theme.border} ${darkMode ? 'bg-slate-800' : 'bg-white'} shadow-sm text-sm`}>
                                        <thead>
                                            <tr className={darkMode ? 'bg-slate-700' : 'bg-slate-100'}>
                                                <th className={`border ${theme.border} p-2 text-left font-bold ${theme.textPrimary}`}>Type</th>
                                                <th className={`border ${theme.border} p-2 text-left font-bold ${theme.textPrimary}`}>Instructie</th>
                                                <th className={`border ${theme.border} p-2 text-left font-bold ${theme.textPrimary}`}>Check/Verificatie</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {specificLoto.map((item, i) => (
                                                <tr key={i} className={darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}>
                                                    <td className={`border ${theme.border} p-2 font-semibold ${theme.textPrimary} whitespace-nowrap`}>{item.type}</td>
                                                    <td className={`border ${theme.border} p-2 ${theme.textPrimary} min-w-[200px]`}>{item.instruction}</td>
                                                    <td className={`border ${theme.border} p-2 ${theme.textPrimary} min-w-[150px]`}>{item.check}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        {lotoSteps && lotoSteps.length > 0 && (
                            <div>
                                <h4 className={`font-bold text-sm ${theme.textSecondary} mb-2 uppercase tracking-wide`}>Algemene LOTO Stappen:</h4>
                                <div className="space-y-2">
                                    {lotoSteps.map((step, i) => (
                                        <div key={i} className={`p-3 rounded border-l-4 border-yellow-400 text-sm ${darkMode ? 'bg-yellow-900/30 text-yellow-100' : 'bg-yellow-50 text-slate-700'}`}>
                                            <span className={`font-bold ${darkMode ? 'text-yellow-200' : 'text-slate-900'}`}>{step.type}:</span> <span>{step.description}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {data.lotoPhotos && data.lotoPhotos.length > 0 && (
                            <div className="mt-12">
                                <h4 className={`font-bold text-sm ${theme.textSecondary} mb-2 uppercase tracking-wide`}>📸 LOTO foto's:</h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    {data.lotoPhotos.map((url, index) => {
                                        const label = getSmartLabel(url);
                                        return (
                                            <div key={index} className={`border ${theme.border} rounded-lg overflow-hidden ${theme.bgSubtle} relative group flex flex-col`}>
                                                <img
                                                    src={url}
                                                    alt={`LOTO foto ${index + 1}`}
                                                    className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity bg-black/20"
                                                    onClick={() => setSelectedImage(url)}
                                                />
                                                <div className="bg-slate-800/80 p-2 text-center border-t border-slate-700">
                                                    <div className="text-xs font-bold text-white truncate" title={label}>
                                                        {label}
                                                    </div>
                                                </div>
                                                {/* Deviation note (formerly voice) */}
                                                {(data.executionChecklist?.[`loto_note_${index}`] || data.executionChecklist?.[`loto_voice_${index}`]) && (
                                                    <div className="p-2 bg-red-900/10 border-t border-red-500/30">
                                                        <div className="text-[10px] text-red-400 uppercase font-bold mb-1 italic">🚨 Melding afwijking:</div>
                                                        <div className="text-xs text-slate-200 italic leading-snug">
                                                            "{data.executionChecklist[`loto_note_${index}`] || data.executionChecklist[`loto_voice_${index}`]}"
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                );
            case 'mjop':
                if (!data.conditionScore) return null;
                const score = parseInt(data.conditionScore);
                let colorClass = 'bg-green-100 text-green-800 border-green-500';
                let label = 'Uitstekend';

                if (score === 2) { label = 'Goed'; colorClass = 'bg-green-50 text-green-700 border-green-400'; }
                if (score === 3) { label = 'Voldoende'; colorClass = 'bg-yellow-50 text-yellow-700 border-yellow-400'; }
                if (score === 4) { label = 'Matig'; colorClass = 'bg-orange-50 text-orange-700 border-orange-400'; }
                if (score === 5) { label = 'Slecht'; colorClass = 'bg-red-50 text-red-700 border-red-500'; }
                if (score === 6) { label = 'Zeer Slecht'; colorClass = 'bg-red-100 text-red-900 border-red-700'; }

                return (
                    <div>
                        <h3 className={`font-bold text-lg border-b-2 ${theme.headingBorder} mb-6 pb-2 ${theme.textPrimary}`}>🏗️ NEN 2767 Conditie</h3>
                        <div className={`p-4 rounded-lg border-l-4 ${colorClass} shadow-sm inline-block min-w-[300px]`}>
                            <div className="flex items-center gap-4">
                                <div className={`flex items-center justify-center w-12 h-12 rounded-full font-bold text-2xl bg-white bg-opacity-50 border-2 border-current`}>
                                    {score}
                                </div>
                                <div>
                                    <div className="font-bold text-lg uppercase tracking-wide">{label}</div>
                                    <div className="text-sm opacity-90">Invloed op MJOP Prognose</div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'risicos': {

                return (
                    <div>
                        <h3 className={`font-bold text-lg border-b-2 border-red-300 mb-6 pb-2 ${theme.textPrimary}`}>
                            ⚡ Risico's & Maatregelen
                        </h3>

                        <div className="space-y-4">
                            {(risks || []).map((risk, i) => (
                                <div key={i} className={`grid grid-cols-12 gap-6 border-b-2 ${theme.border} pb-4 last:border-0`}>
                                    <div className={`col-span-4 font-bold ${theme.accentRed} text-base`}>{risk.Risico}</div>
                                    <div className={`col-span-8 text-base ${theme.textPrimary}`}>{risk.Beheersmaatregel}</div>
                                </div>
                            ))}
                            {(!risks || risks.length === 0) && <div className={`${theme.textSecondary} italic text-center py-4`}>Geen risico's geselecteerd</div>}
                        </div>
                    </div>
                );
            }
            case 'werkstappen':
                // Prioritize Custom Steps (Wizard Edited) over Standard Steps
                const hasCustomSteps = data.customSteps && data.customSteps.trim().length > 0;

                if (!hasCustomSteps && (!steps || steps.length === 0)) return null;

                // Prioritize Selected SOP (Wizard Selected) over Legacy Link
                const sopUrl = data.selectedSop?.url || data.sopLink;
                const sopName = data.selectedSop?.name || 'SOP / OPL Instructie';

                return (
                    <div>
                        <h3 className={`font-bold text-lg border-b-2 ${theme.headingBorder} mb-6 pb-2 ${theme.textPrimary}`}>📝 Werkstappen</h3>
                        {sopUrl && (
                            <div className="mb-6">
                                <a
                                    href={sopUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#38BDF8' }}
                                    className={`flex items-center gap-2 p-3 rounded-lg border font-bold ${darkMode ? 'bg-sky-900/20 border-sky-800' : 'bg-sky-50 border-sky-200'} !text-[#38BDF8] hover:underline transition-colors`}
                                >
                                    <span className="text-xl">📋</span>
                                    <span className="font-semibold">Bekijk {sopName}</span>
                                    <span className="text-xs opacity-70 ml-auto">(Klik om te openen)</span>
                                </a>
                            </div>
                        )}
                        <div className="space-y-6">
                            {hasCustomSteps ? (
                                // Render Custom Steps (String based)
                                data.customSteps.split('\n').filter(line => line.trim()).map((stepLine, i) => (
                                    <div key={i} className="flex gap-3 items-start group">
                                        <div className={`flex-shrink-0 w-8 font-bold text-lg ${theme.textPrimary} text-right select-none opacity-80`}>
                                            {i + 1}
                                        </div>
                                        <div className="flex-1 min-w-0 pt-0.5">
                                            <div className={`text-base leading-relaxed break-words ${theme.textPrimary}`}>
                                                {stepLine}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                // Render Standard Steps (Object based)
                                steps.map((step, i) => (
                                    <div key={i} className="flex gap-3 items-start group">
                                        <div className={`flex-shrink-0 w-8 font-bold text-lg ${theme.textPrimary} text-right select-none opacity-80`}>
                                            {i + 1}
                                        </div>
                                        <div className="flex-1 min-w-0 pt-0.5">
                                            <div className={`text-base leading-relaxed break-words ${theme.textPrimary}`}>
                                                {formatText(step["Stap Omschrijving"])}
                                            </div>
                                            {step["Opmerking (relatie tot parent)"] && (
                                                <div className={`text-sm mt-1 pl-0 text-blue-400 italic`}>
                                                    💡 {formatText(step["Opmerking (relatie tot parent)"])}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                );
            case 'afval':
                const wasteOption = data.task_waste_mapping?.[data.task];
                if (!wasteOption) return null;
                return (
                    <div className={`p-3 border rounded-lg flex items-center gap-2 ${darkMode ? 'bg-emerald-900/20 border-emerald-800/50 text-emerald-300' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`}>
                        <span className="font-bold text-sm">♻️ Afvalverwerking:</span>
                        <span className="text-sm font-medium">{wasteOption}</span>
                    </div>
                );
            case 'handtekeningen':
                // User requested removal of signatures from both Template and Wizard
                return null;
            case 'images': {
                const hasTaskImages = data.taskImageUrls && data.taskImageUrls.length > 0;
                const hasLegacyImage = !hasTaskImages && data.taskImageUrl;

                // Calculate logic for execution photos presence
                const hasExecutionPhotos = data.executionChecklist && Object.keys(data.executionChecklist).some(k => k.startsWith('mechanic_photo_'));

                // FIX: LOTO photos are now rendered in 'loto' section. 'images' is for Situation & Execution only.
                if (!hasTaskImages && !hasLegacyImage && !hasExecutionPhotos) return null;





                return (
                    <div className="space-y-4">
                        <h3 className={`font-bold text-sm uppercase tracking-wider mb-3 flex items-center gap-2 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                            📸 Asset/Onderdeel foto's:
                        </h3>
                        {/* RENDER DEBUG INFO HERE */}


                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">


                            {hasTaskImages && (
                                <div className="mb-6">
                                    <h4 className={`font-bold text-sm ${theme.textSecondary} mb-2 uppercase tracking-wide`}>Asset/Onderdeel foto's:</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        {data.taskImageUrls.map((url, index) => {
                                            const label = getSmartLabel(url);
                                            return (
                                                <div key={index} className={`border ${theme.border} rounded-lg overflow-hidden ${theme.bgSubtle} relative group flex flex-col`}>
                                                    <img
                                                        src={url}
                                                        alt={`Situatie ${index + 1}`}
                                                        className="w-full h-48 object-contain cursor-pointer hover:opacity-90 transition-opacity bg-black/20"
                                                        onClick={() => setSelectedImage(url)}
                                                    />
                                                    <div className="bg-slate-800/80 p-2 text-center border-t border-slate-700">
                                                        <div className="text-xs font-bold text-white truncate" title={label}>
                                                            {label}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {hasLegacyImage && (
                                <div className="mb-6">
                                    <h4 className={`font-bold text-sm ${theme.textSecondary} mb-2 uppercase tracking-wide`}>Asset/Onderdeel foto:</h4>
                                    <div className={`border ${theme.border} rounded-lg overflow-hidden ${theme.bgSubtle} inline-block relative group flex flex-col`}>
                                        <img
                                            src={data.taskImageUrl}
                                            alt="Situatie"
                                            className="max-w-full h-auto max-h-96 object-contain cursor-pointer hover:opacity-90 transition-opacity bg-black/20"
                                            onClick={() => setSelectedImage(data.taskImageUrl)}
                                        />
                                        <div className="bg-slate-800/80 p-2 text-center border-t border-slate-700 w-full">
                                            <div className="text-xs font-bold text-white truncate" title={getSmartLabel(data.taskImageUrl)}>
                                                {getSmartLabel(data.taskImageUrl)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* EXECUTION PHOTOS - FROM MANDATORY CHECKS */}
                            {(() => {
                                const checklist = data.executionChecklist || {};
                                const executionPhotos = [];

                                Object.keys(checklist).forEach(key => {
                                    if (key.startsWith('mechanic_photo_')) {
                                        const index = parseInt(key.split('_')[2], 10);
                                        const label = data.checks && data.checks[index] ? data.checks[index] : `Check #${index + 1}`;
                                        executionPhotos.push({ url: checklist[key], label });
                                    }
                                });

                                if (executionPhotos.length === 0) return null;

                                return (
                                    <div className="mt-8 border-t border-slate-700/50 pt-8">
                                        <h4 className={`font-bold text-sm ${theme.textSecondary} mb-4 uppercase tracking-wide flex items-center gap-2`}>
                                            ✅ Uitvoeringsfoto's
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {executionPhotos.map((item, i) => (
                                                <div key={i} className={`p-4 rounded-lg border ${theme.border} ${theme.bgSubtle}`}>
                                                    <div className="mb-3 font-semibold text-sm line-clamp-2" title={item.label}>
                                                        {item.label}
                                                    </div>
                                                    <div className="rounded overflow-hidden bg-black/50 border border-slate-700/50 w-full aspect-video">
                                                        <img
                                                            src={item.url}
                                                            alt={item.label}
                                                            className="w-full h-full object-contain cursor-pointer hover:scale-105 transition-transform duration-300"
                                                            onClick={() => setSelectedImage(item.url)}
                                                        />
                                                    </div>
                                                    {/* Deviation note display */}
                                                    {(checklist[`mechanic_voice_${i}`]) && (
                                                        <div className="mt-3 p-3 bg-red-900/10 border-l-3 border-red-500 rounded-r text-sm italic text-slate-100">
                                                            <div className="text-[10px] text-red-400 font-bold uppercase not-italic mb-1">⚠️ Melding Afwijking:</div>
                                                            "{checklist[`mechanic_voice_${i}`]}"
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                );
            }
            default:
                return null;
        }
    };

    return (
        <div className={`flex flex-col ${spacingClass}`}>
            {config && config.sections && config.sections.filter(s => s && s.visible).map(section => (
                <div key={section.id}>
                    {renderSection(section.id)}
                </div>
            ))}

            {selectedImage && createPortal(
                <div
                    className="fixed z-[10000] bg-black/95 flex items-center justify-center overflow-hidden"
                    style={{
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        margin: 0,
                        padding: 0,
                        position: 'fixed'
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImage(null);
                    }}
                >
                    <img
                        src={selectedImage}
                        alt="Vergroting"
                        className="max-w-full max-h-full object-contain"
                        style={{ width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '100%' }}
                    />
                    <button
                        className="absolute top-4 right-4 text-white hover:text-gray-300 z-[10001] bg-black/50 rounded-full p-2"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImage(null);
                        }}
                    >
                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>,
                document.body
            )}
        </div>
    );
}
